export type RepoSummary = {
    id: string;
    url: string;
    default_branch: string;
  
    // Optional fields — recommended to include in /v1/repos response
    open_findings?: number;
    open_count?: number;
    fixed_count?: number;
    suppressed_count?: number;
    open_critical?: number;
    open_high?: number;
    open_medium?: number;
    open_low?: number;
    last_scan_at?: string;
    last_scan_status?: "ok" | "partial" | "error" | "unknown";
    /**
     * State of the most recent scan JOB. A scan that fails before results are
     * ingested (unreadable archive, clone failure) never writes a scan record,
     * so last_scan_status stays "unknown" and only this reveals the failure.
     */
    last_job_state?: "pending" | "processing" | "completed" | "failed" | "dead" | null;
    /** The worker's reason when last_job_state is "failed". */
    last_job_error?: string | null;
    last_job_at?: string | null;
  };
  
  export type FindingStatus = "open" | "fixed" | "suppressed" | string;
  
  export type FindingRecord = {
    id: string;
    repo_id: string;
    fingerprint: string;
  
    severity: string; // critical/high/medium/low/info
    status: FindingStatus;
  
    tool: string; // scanner engine identifier
    title: string;
    file_path?: string | null;
    start_line?: number | null;
    end_line?: number | null;
  
    first_seen_at: string;
    last_seen_at?: string; // optional but recommended for burn-down chart
  
    // optional flexible metadata (CVE details, package info, etc.)
    location_data?: Record<string, unknown>;

    // FP scoring (Paxley AI)
    fp_score?: number;       // 0-100, higher = more likely FP
    fp_likely?: boolean;     // true if fp_score >= 40
    fp_reasons?: string[];   // ["test_file", "low_severity", etc.]

    // Reachability (Go dependencies)
    reachability_status?: "reachable" | "no_path_found" | "not_applicable";
    reachability_confidence?: "high" | "medium" | "low";

    // P0 Features - Risk & Priority Scoring
    priority_score?: number;     // 0-100, higher = more urgent
    risk_score?: number;         // 0-100, overall risk
    is_kev?: boolean;            // CISA Known Exploited Vulnerability
    epss_score?: number;         // 0-1, probability of exploitation
    cvss_score?: number;         // 0-10, CVSS base score
    cve_id?: string;             // CVE-YYYY-NNNNN
    is_new?: boolean;            // First seen in latest scan
    is_reachable?: boolean;      // Reachability analysis result
    recommendation?: string;     // AI-generated fix recommendation
    introduced_at?: string;      // When the vuln was introduced
    fixed_at?: string;           // When the vuln was fixed
  };
  
  export type RepoFindingsResponse =
    | FindingRecord[]
    | {
        repo?: RepoSummary;
        findings: FindingRecord[];
        pagination?: { total: number; limit: number; offset: number };
      };
  
  export class ApiError extends Error {
    status: number;
    body?: any;
  
    constructor(message: string, status: number, body?: any) {
      super(message);
      this.name = "ApiError";
      this.status = status;
      this.body = body;
    }
  }
  
// When deployed, API is proxied via nginx on same origin
// In development, use REACT_APP_API_URL if set
const rawBase = process.env.REACT_APP_API_URL || "";
export const API_BASE_URL = rawBase.replace(/\/+$/, "");
  
  export function getApiKey(): string {
    // API keys should NOT be stored in localStorage or baked into the frontend bundle.
    // The dashboard uses session cookies for authentication (set via GitHub OAuth).
    // This function is only kept for backward compatibility with worker/scanner calls
    // that inject the key at runtime (not in the browser).
    // 
    // In the browser, authentication is handled via httpOnly session cookies
    // sent automatically with credentials: "include".
    return "";
  }
  
  type FetchOptions = {
    method?: string;
    path: string;
    query?: Record<string, string | number | boolean | undefined>;
    body?: any;
    apiKey?: string; // optional override
  };
  
  function buildUrl(path: string, query?: FetchOptions["query"]): string {
    const base = API_BASE_URL.replace(/\/+$/, "");
    const p = path.startsWith("/") ? path : `/${path}`;
    
    // Handle relative URLs (when API is proxied on same origin)
    const fullPath = base ? `${base}${p}` : p;
    const url = base ? new URL(fullPath) : new URL(fullPath, window.location.origin);
  
    if (query) {
      for (const [k, v] of Object.entries(query)) {
        if (v === undefined) continue;
        url.searchParams.set(k, String(v));
      }
    }
  
    return url.toString();
  }
  
  export async function apiFetch<T>(opts: FetchOptions): Promise<T> {
    const apiKey = (opts.apiKey ?? getApiKey()).trim();
    const url = buildUrl(opts.path, opts.query);
  
    const headers: Record<string, string> = {
      "content-type": "application/json"
    };
    // Only send API key header if we have one (worker auth)
    // For user auth, we rely on cookies
    if (apiKey) headers["x-paxley-key"] = apiKey;
  
    let res: Response;
    try {
      res = await fetch(url, {
        method: opts.method || "GET",
        headers,
        body: opts.body ? JSON.stringify(opts.body) : undefined,
        credentials: "include" // Send cookies for session auth
      });
    } catch (e: any) {
      throw new ApiError(`Network error calling Paxley API: ${e?.message || e}`, 0);
    }
  
    const contentType = res.headers.get("content-type") || "";
    const isJson = contentType.includes("application/json");
  
    const text = await res.text();
    const data = isJson && text ? safeJsonParse(text) : text;
  
    if (!res.ok) {
      const message =
        (isJson && (data?.message || data?.error)) ||
        (typeof data === "string" && data) ||
        `HTTP ${res.status}`;
      throw new ApiError(message, res.status, data);
    }
  
    return (isJson ? (data as T) : (text as any as T));
  }
  
  function safeJsonParse(text: string): any {
    try {
      return JSON.parse(text);
    } catch {
      return { raw: text };
    }
  }
  
  // -------- API functions --------
  
  export async function listRepos(): Promise<RepoSummary[]> {
    const data = await apiFetch<any>({ path: "/v1/repos" });
  
    // Allow either {repos:[...]} or [...]
    if (Array.isArray(data)) return data as RepoSummary[];
    if (data && Array.isArray(data.repos)) return data.repos as RepoSummary[];
    return [];
  }

  export type SyncReposResult = {
    success: boolean;
    synced: number;
    errors?: string[];
  };

  export async function syncReposFromGitHub(): Promise<SyncReposResult> {
    return apiFetch<SyncReposResult>({
      path: "/v1/repos/sync",
      method: "POST",
      body: {},
    });
  }
  
  export async function listRepoFindings(
    repoId: string,
    filters?: {
      severity?: string;
      tool?: string; // "sast/sca/secret" or engine name depending on backend
      status?: string;
      q?: string;
      limit?: number;
      offset?: number;
    }
  ): Promise<{ repo?: RepoSummary; findings: FindingRecord[]; pagination?: { total: number; limit: number; offset: number } }> {
    const data = await apiFetch<RepoFindingsResponse>({
      path: `/v1/repos/${repoId}/findings`,
      query: {
        severity: filters?.severity,
        tool: filters?.tool,
        status: filters?.status,
        q: filters?.q,
        limit: filters?.limit ?? 100,
        offset: filters?.offset ?? 0,
      }
    });
  
    if (Array.isArray(data)) return { findings: data as FindingRecord[] };
    return { repo: data.repo, findings: data.findings || [], pagination: data.pagination };
  }
  
  export async function suppressFinding(
    findingId: string,
    payload: { reason: string; expires_at?: string | null }
  ): Promise<any> {
    // Endpoint is intentionally unversioned per your note: POST /findings/:id/suppress
    return apiFetch<any>({
      method: "POST",
      path: `/findings/${findingId}/suppress`,
      body: payload
    });
  }
  
  export function isAuthError(err: unknown): err is ApiError {
    return err instanceof ApiError && (err.status === 401 || err.status === 403);
  }

  // -------- Metrics API (burn-down chart) --------

  export type MetricsPoint = {
    date: string;
    open: number;
    fixed: number;
    total_introduced: number;
  };

  export type MetricsResponse = {
    repo_id: string;
    days: number;
    points: MetricsPoint[];
    current: {
      open: number;
      fixed: number;
      by_severity: Record<string, number>;
    };
  };

  export async function getRepoMetrics(repoId: string, days = 90): Promise<MetricsResponse> {
    return apiFetch<MetricsResponse>({
      path: `/v1/repos/${repoId}/metrics`,
      query: { days }
    });
  }

  // -------- Export API (CSV/JSON compliance export) --------

  export async function exportFindings(
    repoId: string,
    options?: { format?: "csv" | "json"; status?: "open" | "fixed" | "suppressed" | "all" }
  ): Promise<Blob | Record<string, unknown>> {
    const format = options?.format || "csv";
    const base = API_BASE_URL.replace(/\/+$/, "");
    const path = `${base}/v1/repos/${repoId}/export`;
    // Handle both absolute and relative URLs
    const url = base ? new URL(path) : new URL(path, window.location.origin);
    url.searchParams.set("format", format);
    if (options?.status) url.searchParams.set("status", options.status);

    const apiKey = getApiKey();
    const headers: Record<string, string> = {};
    if (apiKey) headers["x-paxley-key"] = apiKey;

    const res = await fetch(url.toString(), { 
      method: "GET", 
      headers,
      credentials: "include" // Send cookies for session auth
    });

    if (!res.ok) {
      const text = await res.text();
      throw new ApiError(text || `HTTP ${res.status}`, res.status);
    }

    if (format === "json") {
      return res.json();
    }

    return res.blob();
  }

  export async function downloadExport(
    repoId: string,
    options?: { format?: "csv" | "json"; status?: "open" | "fixed" | "suppressed" | "all" }
  ): Promise<void> {
    const format = options?.format || "csv";
    const data = await exportFindings(repoId, options);

    // exportFindings returns a Blob for CSV (raw response body) and a parsed
    // JSON object for JSON (res.json()). Previously this branch only fired
    // for Blob, so JSON downloads silently did nothing. Wrap the parsed
    // object back into a Blob so both formats hit the same download path.
    const blob =
      data instanceof Blob
        ? data
        : new Blob([JSON.stringify(data, null, 2)], { type: "application/json" });

    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `paxley-export-${repoId.slice(0, 8)}.${format}`;
    document.body.appendChild(a);
    a.click();
    a.remove();
    URL.revokeObjectURL(url);
  }

  /** Fetch export as parsed JSON (for HTML report builder) */
  export async function exportFindingsJSON(
    repoId: string,
    options?: { status?: "open" | "fixed" | "suppressed" | "all" }
  ): Promise<FindingRecord[]> {
    const data = await exportFindings(repoId, { format: "json", status: options?.status });
    if (Array.isArray(data)) return data as FindingRecord[];
    if (data && typeof data === "object" && "findings" in data) {
      return (data as any).findings as FindingRecord[];
    }
    return [];
  }

  // -------- Scan Trigger API --------

  export type TriggerScanResponse = {
    job_id: string;
    status: "queued" | "running" | "completed" | "failed";
    message?: string;
  };

  export type ScanStatusResponse = {
    job_id: string;
    status: "queued" | "running" | "completed" | "failed";
    progress?: number;
    findings_count?: number;
    started_at?: string;
    completed_at?: string;
    error?: string;
  };

  /**
   * Trigger a baseline scan for a repository
   */
  export async function triggerScan(
    repoId: string,
    options?: { scanType?: "baseline" | "pr"; branch?: string }
  ): Promise<TriggerScanResponse> {
    return apiFetch<TriggerScanResponse>({
      method: "POST",
      path: `/v1/repos/${repoId}/scan`,
      body: {
        scan_type: options?.scanType || "baseline",
        branch: options?.branch
      }
    });
  }

  /**
   * Get scan job status
   */
  export async function getScanStatus(jobId: string): Promise<ScanStatusResponse> {
    return apiFetch<ScanStatusResponse>({
      path: `/v1/jobs/${jobId}/status`
    });
  }

  /**
   * Delete an uploaded / git-URL repository and everything derived from it.
   * Without this a repository created by a failed upload could never be
   * removed and permanently consumed part of the plan's allowance.
   */
  export async function deleteRepo(repoId: string): Promise<{ ok: boolean }> {
    return apiFetch<{ ok: boolean }>({
      method: "DELETE",
      path: `/v1/repos/${repoId}`,
    });
  }

  export type ActiveScanResponse = {
    active: boolean;
    job_id: string | null;
    state?: string | null;
    started_at?: string | null;
  };

  /**
   * Whether a scan is queued/running for this repo. Lets the repo page show
   * live progress for a scan it did not start itself (e.g. one kicked off from
   * the upload page, or in another tab/device).
   */
  export async function getActiveScan(repoId: string): Promise<ActiveScanResponse> {
    return apiFetch<ActiveScanResponse>({
      path: `/v1/repos/${repoId}/active-scan`
    });
  }

  // -------- PR Scans API --------

  export type PrScan = {
    id: string;
    commit_sha: string;
    status: string;
    findings_count: number;
    critical: number;
    high: number;
    medium: number;
    low: number;
    created_at: string | null;
    updated_at: string | null;
  };

  export type PrScanFinding = {
    id: string;
    fingerprint: string;
    severity: string;
    status: string;
    tool: string;
    title: string;
    file_path: string | null;
    start_line: number | null;
    end_line: number | null;
    kind: string | null;
    rule_id: string | null;
    code_snippet: string | null;
    fp_score: number;
    fp_likely: boolean;
    priority_score: number | null;
    risk_score: number | null;
    cve_id: string | null;
  };

  export async function listPrScans(repoId: string): Promise<{ scans: PrScan[] }> {
    return apiFetch<{ scans: PrScan[] }>({ path: `/v1/repos/${repoId}/pr-scans` });
  }

  export async function getScanFindings(repoId: string, scanId: string): Promise<{ findings: PrScanFinding[]; total: number }> {
    return apiFetch<{ findings: PrScanFinding[]; total: number }>({ path: `/v1/repos/${repoId}/scans/${scanId}/findings` });
  }

  // -------- Saved Views API --------

  export type SavedView = {
    id: string;
    name: string;
    filters: {
      severities?: string[];
      statuses?: string[];
      kinds?: string[];
      tools?: string[];
      search?: string;
      minPriority?: number;
      kevOnly?: boolean;
    };
    is_default?: boolean;
    created_at: string;
  };

  export async function listSavedViews(): Promise<SavedView[]> {
    try {
      const data = await apiFetch<{ views: SavedView[] } | SavedView[]>({
        path: `/v1/views`
      });
      if (Array.isArray(data)) return data;
      return data.views || [];
    } catch {
      return [];
    }
  }

  export async function createSavedView(
    view: { name: string; filters: SavedView["filters"]; is_default?: boolean }
  ): Promise<SavedView> {
    return apiFetch<SavedView>({
      method: "POST",
      path: `/v1/views`,
      body: view
    });
  }

  export async function deleteSavedView(viewId: string): Promise<void> {
    await apiFetch<void>({
      method: "DELETE",
      path: `/v1/views/${viewId}`
    });
  }

  export async function updateSavedView(
    viewId: string, 
    updates: Partial<SavedView>
  ): Promise<SavedView> {
    return apiFetch<SavedView>({
      method: "PUT",
      path: `/v1/views/${viewId}`,
      body: updates
    });
  }

  // -------- Priority Queue API --------

  export type PriorityFinding = FindingRecord & {
    priority_score: number;
    risk_score: number;
  };

  export async function getPriorityQueue(
    repoId: string,
    options?: { limit?: number; minScore?: number }
  ): Promise<PriorityFinding[]> {
    try {
      const data = await apiFetch<{ findings: PriorityFinding[] } | PriorityFinding[]>({
        path: `/v1/repos/${repoId}/priority-queue`,
        query: {
          limit: options?.limit || 50,
          min_score: options?.minScore
        }
      });
      if (Array.isArray(data)) return data;
      return data.findings || [];
    } catch {
      return [];
    }
  }
  